import { DBSchema, IDBPDatabase, IndexKey, openDB, StoreNames, StoreValue } from 'idb';
import { from, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';


/**
 * Service for interacting with the local IDb database
 * Interaction built with https://github.com/jakearchibald/idb
 */
export class IdbFactory<DBtype extends DBSchema, T> {
    /**
     * Database name
     */
    readonly #dbName: string;
    /**
     * Database version
     */
    readonly #dbVersion: number;
    /**
     * Table connect name
     */
    readonly #tableName: string;
    /**
     * Connection object
     *
     * @private
     */
    readonly #connection: Observable<IDBPDatabase<DBtype>>;

    constructor(dbName: string, dbVersion: number, tableName: string) {
        this.#dbName = dbName;
        this.#dbVersion = dbVersion;
        this.#tableName = tableName;
        this.#connection = this.connectToIDB();
    }

    /**
     * Connect for IDB storage
     */
    public connectToIDB(
        mode?: 'readwrite' | 'readonly',
    ): Observable<IDBPDatabase<DBtype>> {
        return from(
            openDB<DBtype>(this.#dbName, this.#dbVersion, {
                upgrade: this.upgrade.bind(this),
            }),
        ).pipe(
            map((db) => {
                db.transaction(
                    this.#tableName as StoreNames<DBtype>,
                    mode || 'readwrite',
                ).objectStore(this.#tableName as StoreNames<DBtype>);
                return db;
            }),
        );
    }

    /**
     * Upgrade locally DB
     * More details https://github.com/jakearchibald/idb#api
     *
     * @param db - database object
     * @param oldVersion - old version
     */
    public upgrade(db: IDBPDatabase, oldVersion: number) {
        if (oldVersion < 1) {
            const clientsSupportStore = db.createObjectStore(this.#tableName, {
                keyPath: 'id',
            });
            clientsSupportStore.createIndex('by-id', 'id');
        }
    }

    /**
     * Put the value in the local database
     */
    public store(data: StoreValue<DBtype, any>): Observable<unknown> {
        return this.#connection.pipe(
            switchMap((db) => from(db.put(this.#tableName as any, data))),
        );
    }

    /**
     * Delete data from local storage by key
     */
    public retrieve(key: IndexKey<DBtype, any, any>): Observable<unknown | T> {
        return this.#connection.pipe(
            switchMap((db) => from(db.getFromIndex(this.#tableName, 'by-id', key))),
        );
    }

    /**
     * Get list of values from local database
     */
    public getData(): Observable<StoreValue<DBtype, any>[]> {
        return this.#connection.pipe(
            switchMap((db) =>
                from(db.getAllFromIndex(this.#tableName as any, 'by-id' as any, null)),
            ),
        );
    }
}
